import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { ACCENT } from './discussionUtils';

const TOPIC_HINTS = [
  'Public & visible to everyone',
  'Use tags (Question, Solution, etc.)',
  'Explain your approach — not just code',
  'No personal contact info',
];

const COMMENT_HINTS = [
  'Public & visible to everyone',
  'Be respectful and on-topic',
  'No spam, harassment, or ads',
  'No emails, phone numbers, or DMs',
];

const REPLY_HINTS = [
  'Public reply',
  'Be respectful',
  'No personal contact info',
];

/**
 * Compact posting guidelines shown above discussion composers.
 */
const DiscussionPostingGuidelines = ({ variant = 'comment', isDark, border }) => {
  const hints =
    variant === 'topic' ? TOPIC_HINTS : variant === 'reply' ? REPLY_HINTS : COMMENT_HINTS;
  const muted = isDark ? '#8b949e' : '#5f6b73';

  return (
    <Box
      sx={{
        px: 1.5,
        pt: 1.1,
        pb: 0.35,
        borderBottom: `1px solid ${alpha(border, 0.65)}`,
        bgcolor: isDark ? alpha('#fff', 0.02) : alpha(ACCENT, 0.03),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
        <PublicOutlinedIcon sx={{ fontSize: 13, color: ACCENT }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: isDark ? '#c9d1d9' : '#0f2a2b', fontSize: '0.68rem' }}
        >
          {variant === 'topic' ? 'Before you publish' : variant === 'reply' ? 'Before you reply' : 'Before you post'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {hints.map((hint) => (
          <Typography
            key={hint}
            component="span"
            variant="caption"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 0.75,
              py: 0.2,
              borderRadius: 1,
              fontSize: '0.65rem',
              fontWeight: 600,
              lineHeight: 1.35,
              color: muted,
              bgcolor: isDark ? alpha('#fff', 0.04) : '#fff',
              border: `1px solid ${alpha(border, 0.85)}`,
            }}
          >
            {hint}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default DiscussionPostingGuidelines;

/** Single-line notice for the discussion tab header (always visible). */
export const DiscussionCommunityNotice = ({ isDark, border }) => {
  const muted = isDark ? '#8b949e' : '#5f6b73';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.6,
        mb: 1,
        px: 1,
        py: 0.65,
        borderRadius: 1.25,
        border: `1px solid ${alpha(border, 0.9)}`,
        bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.04),
      }}
    >
      <PublicOutlinedIcon sx={{ fontSize: 14, color: ACCENT, mt: 0.1, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color: muted, lineHeight: 1.45, fontSize: '0.68rem' }}>
        Discussions are <strong style={{ color: isDark ? '#c9d1d9' : '#0f2a2b' }}>public</strong>.
        Be respectful, stay on topic, and don&apos;t share personal contact info, spam, or harassment.
      </Typography>
    </Box>
  );
};
